import { useCloseUnlessOrgNode } from '../hooks/useCloseWhenMissing'
import { nodePagePath } from '../utilities/nodeGroups'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useModalClose } from '../hooks/useModalClose'
import useNode from '../models/useNode'
import useServices from '../models/useServices'
import DialogContent from '@mui/material/DialogContent'
import IconService from './IconService'
import AddService from './AddService'
import EditService from './EditService'
import Modal from './Modal'
import Typography from '@mui/material/Typography'
import { useOrgRole } from '../hooks/useOrgRole'
import { SERVICE_FORBIDDEN } from '../utilities/routerWrites'

type Props = {
  inline?: boolean
}

export default function ModalService({ inline }: Readonly<Props>) {
  const { nodeId, serviceId } = useParams()
  // ⚠ An id in the path that is not an object of the active org sends the reader home (app#475).
  useCloseUnlessOrgNode(nodeId)
  const { nodes } = useNode()
  const { getByNodeId } = useServices()

  const service = serviceId && nodeId ? getByNodeId(nodeId).find(s => s.id === serviceId) : undefined
  const [serviceType, setServiceType] = useState<ServiceType | undefined>(
    service ? (service.url ? 'web' : 'terminal') : undefined
  )

  const node = nodes.find(n => n.id === nodeId)
  // Only the node's controllers publish or change its services (app#373). The node page hides the
  // way in for everyone else, but this route is a URL, so it says so instead of offering a form
  // whose save the router refuses. A node not loaded yet shows the form, as before. The Owner/Admin
  // half is asked in the node's TENANT, `node.ownerOrgId` (G-1, app#385; Benoit, 2026-09-26).
  const { controls } = useOrgRole(node ? (node.ownerOrgId ?? null) : undefined)
  const canChange = !node || controls(node.ownerUserId)
  const { open, handleClose } = useModalClose(nodeId ? nodePagePath(nodeId) : '/nodes', inline)

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="md"
      onClose={inline ? undefined : handleClose}
      color="secondary.main"
      icon={<IconService width={54} height={54} outline plus={!serviceId} type={serviceType || 'web'} />}
    >
      <DialogContent>
        {!canChange ? (
          <Typography variant="body1">{SERVICE_FORBIDDEN}</Typography>
        ) : serviceId && service ? (
          <EditService service={service} nodeId={nodeId!} nodeName={node?.name} onSuccess={handleClose} />
        ) : (
          <AddService
            nodeId={nodeId}
            nodeName={node?.name}
            type={serviceType}
            onTypeChange={type => {
              setServiceType(type)
            }}
            onSuccess={handleClose}
          />
        )}
      </DialogContent>
    </Modal>
  )
}
